import { useState } from 'react';
import { Printer } from 'lucide-react';
import type { TenantPrinterWithDetails } from '@maker-toolchest/shared';
import { Button } from '@/components/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/Tabs';
import { PrinterStatusBadge } from './PrinterStatusBadge';
import { AccessorySlotDisplay } from './AccessorySlotDisplay';
import { AccessoryManager } from './AccessoryManager';
import { PrinterNotes } from './PrinterNotes';
import { PrintHistoryTable } from './PrintHistoryTable';
import { EmptyState } from '@/components/EmptyState';
import { useTier } from '@/hooks/useAuth';
import { formatDate } from '@/utils/formatting';

interface PrinterDetailViewProps {
  printer: TenantPrinterWithDetails;
  onEdit?: () => void;
  onClose?: () => void;
}

export function PrinterDetailView({ printer, onEdit, onClose }: PrinterDetailViewProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const { isBusiness } = useTier();

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-border bg-background-subtle px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-background">
              <Printer className="h-8 w-8 text-foreground-muted" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-foreground">
                {printer.instanceName || printer.printer.name}
              </h2>
              <p className="text-sm text-foreground-muted">
                {printer.printer.brand?.name || 'Custom'} {printer.printer.model?.name || 'Build'}
              </p>
              {printer.serialNumber && (
                <p className="mt-1 text-xs text-foreground-muted">
                  SN: {printer.serialNumber}
                </p>
              )}
              <div className="mt-2">
                <PrinterStatusBadge status={printer.status} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onEdit && (
              <Button variant="secondary" size="sm" onClick={onEdit}>
                Edit
              </Button>
            )}
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-1 flex-col overflow-hidden">
        <div className="border-b border-border px-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="jobs">Jobs</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
            <TabsTrigger value="accessories">
              Accessories {printer.accessoryCount > 0 && `(${printer.accessoryCount})`}
            </TabsTrigger>
            <TabsTrigger value="notes">
              Notes {printer.noteCount > 0 && `(${printer.noteCount})`}
            </TabsTrigger>
            <TabsTrigger value="history">Print History</TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Overview Tab */}
          <TabsContent value="overview" className="p-6">
            <div className="space-y-6">
              {/* Installed Accessories */}
              <div>
                <h3 className="mb-3 text-lg font-medium text-foreground">Installed Accessories</h3>
                <AccessorySlotDisplay printer={printer} />
              </div>

              {/* Specifications */}
              <div>
                <h3 className="mb-3 text-lg font-medium text-foreground">Specifications</h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {printer.printer.buildVolumeX && printer.printer.buildVolumeY && printer.printer.buildVolumeZ && (
                    <div className="rounded-lg border border-border bg-background-subtle p-4">
                      <div className="text-xs text-foreground-muted">Build Volume</div>
                      <div className="mt-1 text-sm font-medium text-foreground">
                        {printer.printer.buildVolumeX} × {printer.printer.buildVolumeY} × {printer.printer.buildVolumeZ} mm
                      </div>
                    </div>
                  )}
                  {printer.printer.technology && (
                    <div className="rounded-lg border border-border bg-background-subtle p-4">
                      <div className="text-xs text-foreground-muted">Technology</div>
                      <div className="mt-1 text-sm font-medium text-foreground">
                        {printer.printer.technology}
                      </div>
                    </div>
                  )}
                  {printer.printer.nozzleTempMax && (
                    <div className="rounded-lg border border-border bg-background-subtle p-4">
                      <div className="text-xs text-foreground-muted">Max Nozzle Temp</div>
                      <div className="mt-1 text-sm font-medium text-foreground">
                        {printer.printer.nozzleTempMax}°C
                      </div>
                    </div>
                  )}
                  {printer.printer.bedTempMax && (
                    <div className="rounded-lg border border-border bg-background-subtle p-4">
                      <div className="text-xs text-foreground-muted">Max Bed Temp</div>
                      <div className="mt-1 text-sm font-medium text-foreground">
                        {printer.printer.bedTempMax}°C
                      </div>
                    </div>
                  )}
                  {printer.printer.maxPrintSpeed && (
                    <div className="rounded-lg border border-border bg-background-subtle p-4">
                      <div className="text-xs text-foreground-muted">Max Print Speed</div>
                      <div className="mt-1 text-sm font-medium text-foreground">
                        {printer.printer.maxPrintSpeed} mm/s
                      </div>
                    </div>
                  )}
                  {printer.printer.maxTravelSpeed && (
                    <div className="rounded-lg border border-border bg-background-subtle p-4">
                      <div className="text-xs text-foreground-muted">Max Travel Speed</div>
                      <div className="mt-1 text-sm font-medium text-foreground">
                        {printer.printer.maxTravelSpeed} mm/s
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Usage Statistics */}
              <div>
                <h3 className="mb-3 text-lg font-medium text-foreground">Usage Statistics</h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg border border-border bg-background-subtle p-4">
                    <div className="text-xs text-foreground-muted">Total Print Hours</div>
                    <div className="mt-2 text-xl font-semibold text-foreground">
                      {printer.totalPrintHours.toFixed(1)}h
                    </div>
                  </div>
                  <div className="rounded-lg border border-border bg-background-subtle p-4">
                    <div className="text-xs text-foreground-muted">Total Jobs</div>
                    <div className="mt-2 text-xl font-semibold text-foreground">
                      {printer.totalJobCount}
                    </div>
                  </div>
                  <div className="rounded-lg border border-border bg-background-subtle p-4">
                    <div className="text-xs text-foreground-muted">Last Used</div>
                    <div className="mt-2 text-sm font-medium text-foreground">
                      {formatDate(printer.lastUsedDate)}
                    </div>
                  </div>
                  {printer.storageLocation && (
                    <div className="rounded-lg border border-border bg-background-subtle p-4">
                      <div className="text-xs text-foreground-muted">Location</div>
                      <div className="mt-2 text-sm font-medium text-foreground">
                        {printer.storageLocation.name}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Purchase Info */}
              {(printer.purchaseDate || (isBusiness && printer.purchasePrice)) && (
                <div>
                  <h3 className="mb-3 text-lg font-medium text-foreground">Purchase Information</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {printer.purchaseDate && (
                      <div className="rounded-lg border border-border bg-background-subtle p-4">
                        <div className="text-xs text-foreground-muted">Purchase Date</div>
                        <div className="mt-2 text-sm font-medium text-foreground">
                          {formatDate(printer.purchaseDate)}
                        </div>
                      </div>
                    )}
                    {isBusiness && printer.purchasePrice && (
                      <div className="rounded-lg border border-border bg-background-subtle p-4">
                        <div className="text-xs text-foreground-muted">Purchase Price</div>
                        <div className="mt-2 text-sm font-medium text-foreground">
                          ${printer.purchasePrice.toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                  {!isBusiness && (
                    <div className="mt-4 rounded-lg border border-border bg-background-subtle p-4">
                      <p className="text-sm text-foreground-muted">
                        💼 Cost tracking is available on the Business tier.{' '}
                        <a href="/settings/subscription" className="text-primary hover:underline">
                          Upgrade to view costs
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Notes from printer catalog */}
              {printer.printer.notes && (
                <div>
                  <h3 className="mb-3 text-lg font-medium text-foreground">Printer Notes</h3>
                  <div className="rounded-lg border border-border bg-background-subtle p-4">
                    <p className="text-sm text-foreground-muted">{printer.printer.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">Jobs</h3>
              </div>
              <EmptyState
                icon={<Printer className="h-10 w-10" />}
                title="No jobs yet"
                description="Jobs for this printer will appear here once you start tracking print jobs."
              />
            </div>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">Projects</h3>
              </div>
              <EmptyState
                icon={<Printer className="h-10 w-10" />}
                title="No projects yet"
                description="Projects associated with this printer will appear here."
              />
            </div>
          </TabsContent>

          {/* Accessories Tab */}
          <TabsContent value="accessories" className="p-6">
            <AccessoryManager printerId={printer.id} />
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="p-6">
            <PrinterNotes printerId={printer.id} />
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="p-6">
            <PrintHistoryTable printerId={printer.id} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
